import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

export const useContextTabStyles = makeStyles({
  contextHeading: {
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.bold,
    color: designTokens.neutral[900],
    display: "block",
  },
  contextSubtext: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    lineHeight: "1",
    display: "block",
  },
  connectionGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[2],
  },
  connectionCard: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[4],
    borderRadius: designTokens.borderRadius.lg,
    border: `1px solid ${designTokens.neutral[200]}`,
    backgroundColor: designTokens.neutral.white,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    userSelect: "none" as const,
    ":hover": {
      border: `1px solid ${designTokens.neutral[300]}`,
      backgroundColor: designTokens.neutral[50],
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: designTokens.accent[500],
      outlineOffset: "2px",
    },
  },
  connectionCardComingSoon: {
    cursor: "not-allowed",
    backgroundColor: designTokens.neutral[50],
    ":hover": {
      border: `1px solid ${designTokens.neutral[200]}`,
      backgroundColor: designTokens.neutral[50],
    },
  },
  connectionCardSelected: {
    border: `2px solid ${designTokens.accent[500]}`,
    padding: `calc(${designTokens.spacing[4]} - 1px)`,
    backgroundColor: designTokens.accent[50],
    ":hover": {
      border: `2px solid ${designTokens.accent[500]}`,
      backgroundColor: designTokens.accent[50],
    },
  },
  connectionIconTile: {
    width: "40px",
    height: "40px",
    minWidth: "40px",
    borderRadius: designTokens.borderRadius.md,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: designTokens.neutral[100],
    color: designTokens.neutral[500],
    "& svg": {
      width: "20px",
      height: "20px",
    },
  },
  connectionIconTileSelected: {
    backgroundColor: designTokens.accent[500],
    color: designTokens.neutral.white,
  },
  connectionMeta: {
    flex: 1,
    minWidth: 0,
  },
  connectionLabelRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: `2px ${designTokens.spacing[2]}`,
  },
  connectionLabel: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[800],
    lineHeight: "1.3",
    whiteSpace: "nowrap" as const,
  },
  comingSoonBadge: {
    fontSize: "9px",
    fontWeight: designTokens.fontWeight.bold,
    letterSpacing: ".04em",
    textTransform: "uppercase" as const,
    color: designTokens.neutral[500],
    backgroundColor: designTokens.neutral[100],
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.sm,
    padding: "1px 5px",
    flexShrink: 0,
    lineHeight: "1.5",
    whiteSpace: "nowrap" as const,
  },
  connectionDesc: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    marginTop: "2px",
    display: "block",
    lineHeight: "1.4",
  },
  uploadSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: designTokens.spacing[1],
  },
  contextDropzone: {
    border: `1px dashed ${designTokens.neutral[300]}`,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: designTokens.neutral[50],
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[4]}`,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[2],
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    textAlign: "center" as const,
    ":hover": {
      border: `1px dashed ${designTokens.accent[400]}`,
      backgroundColor: designTokens.accent[50],
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: designTokens.accent[500],
      outlineOffset: "2px",
    },
  },
  contextDropzoneDragOver: {
    border: `1px dashed ${designTokens.accent[400]}`,
    backgroundColor: designTokens.accent[50],
  },
  contextDropzoneDisabled: {
    cursor: "not-allowed",
    opacity: 0.6,
  },
  contextDropzoneIcon: {
    color: designTokens.neutral[400],
    "& svg": {
      width: "24px",
      height: "24px",
    },
  },
  contextDropzonePrimary: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
    display: "block",
  },
  contextDropzoneSub: {
    fontSize: designTokens.fontSize.xxs,
    color: designTokens.neutral[400],
    display: "block",
  },
  contextFileInput: {
    display: "none",
  },
  contextFileError: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.semantic.error,
    display: "block",
    lineHeight: "1.4",
  },
  stagedFileList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  stagedFileRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[2]}`,
    backgroundColor: designTokens.neutral[50],
    borderRadius: designTokens.borderRadius.sm,
    border: `1px solid ${designTokens.neutral[200]}`,
  },
  stagedFileName: {
    flex: 1,
    minWidth: 0,
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[700],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  stagedFileRemove: {
    flexShrink: 0,
    minWidth: "auto",
    padding: "0",
    width: "20px",
    height: "20px",
    color: designTokens.neutral[400],
    ":hover": {
      color: designTokens.semantic.error,
      backgroundColor: designTokens.semantic.errorLight,
    },
  },
  contextInfoBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    backgroundColor: designTokens.neutral[100],
    borderRadius: designTokens.borderRadius.md,
    color: designTokens.neutral[600],
    fontSize: designTokens.fontSize.xxs,
    lineHeight: "1.5",
  },
  contextInfoBannerIcon: {
    color: designTokens.accent[500],
    flexShrink: 0,
    marginTop: "1px",
    "& svg": {
      width: "20px",
      height: "20px",
    },
  },
  sectionDivider: {
    display: "block",
    height: "1px",
    backgroundColor: designTokens.neutral[200],
    margin: 0,
  },
});
